import React from 'react';
import PropTypes from 'prop-types';
import memoize from 'memoize-one';
/* components */
import Portal from '../portal/portal';
import ScrollArea from '../scroll-area/scroll-area';
import Input from '../input/input';
import SelectOption from './select-option/select-option';
import SelectValue from './select-value/select-value';
/* utils */
import keyCodes from '../../utils/key-codes';
import { getClassName } from '../../utils/info-helper';
/* styles */
import './select.scss';

class Select extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            isMenuShown: false,
            searchCriteria: '',
            collapseDown: this.props.collapseDown,
            inputFocused: false,
            highlightedItemIndex: -1
        };
    }

    componentDidUpdate(prevProps) {
        if (this.scrollArea && (prevProps.options !== this.props.options)) {
            this.scrollArea.scrollTop();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.documentOnClick);
        document.removeEventListener('scrollChanged', this.handleScrollChange);
        if (this.debounce) {
            clearTimeout(this.debounce);
        }
        if (this.focusDebounce) {
            clearTimeout(this.focusDebounce);
        }
    }

    onInputChange = value => {
        this.setState({
            searchCriteria: value,
            highlightedItemIndex: -1
        });
        if (this.scrollArea && this.scrollArea.state.topPosition !== 0) {
            this.scrollArea.scrollTop();
        }
        if (value.length > 0) {
            this.toggleSelectMenu(true);
        }
        if (this.props.onSearch) {
            if (this.debounce) {
                clearTimeout(this.debounce);
            }
            const trimmedValue = value.trim();
            if (trimmedValue.length >= 3) {
                this.debounce = setTimeout(() => {
                    this.props.onSearch(trimmedValue);
                }, 500);
            }
        }
    };

    handleInputFocusChange = isFocused => {
        if (this.focusDebounce) {
            clearTimeout(this.focusDebounce);
        }
        this.focusDebounce = setTimeout(() => {
            if (isFocused && !this.state.inputFocused) {
                const searchCriteria = this.props.setSearchCriteriaOnFocus
                && this.props.value
                && this.props.value[this.props.labelKey]
                    ? this.props.value[this.props.labelKey]
                    : '';
                this.setState({ inputFocused: true, searchCriteria });
            } else if (!isFocused && this.state.inputFocused) {
                this.setState({ inputFocused: false });
            }
        }, 200);
    };

    onOuterDivFocus = () => {
        if (this.selectInput) {
            this.selectInput.setFocus();
        }
    };

    onOuterDivBlur = e => {
        const isKeyboardClick = e.relatedTarget && typeof e.relatedTarget.className === 'string'
            && e.relatedTarget.className.indexOf('keyboard-button') >= 0;
        if (!isKeyboardClick && this.select && !this.select.contains(e.target)
            && this.selectOptionsRef && !this.selectOptionsRef.contains(e.target)) {
            this.setState({ inputFocused: false });
            this.toggleSelectMenu(false);
        }
    };

    onSelectClick = () => {
        if (!this.props.disabled) {
            this.onOuterDivFocus();
            this.toggleSelectMenu();
        }
    };

    onMenuClick = (e) => {
        e.stopPropagation();
    };

    getOptions = () => this.getOptionsMemoized(this.props.options, this.state.searchCriteria);

    getOptionsMemoized = memoize((optionsUnfiltered, searchCriteria) => {
        let options = [...optionsUnfiltered] || [];

        if (this.props.searchable && searchCriteria.length > 0) {
            const searchCriteriaTrimmed = searchCriteria.trim();
            const searchCriteriaTrimmedUC = searchCriteriaTrimmed.toUpperCase();
            if (!this.props.onSearch) {
                options = options.filter(
                    option => option[this.props.labelKey].toUpperCase().indexOf(searchCriteriaTrimmedUC) >= 0
                );
            }
            if (this.props.includeSearchInResults) {
                const matchingOption = options.find(
                    option => option[this.props.labelKey].toUpperCase() === searchCriteriaTrimmedUC
                );
                if (!matchingOption) {
                    const searchCriteriaOption = {};
                    searchCriteriaOption[this.props.labelKey] = searchCriteriaTrimmed;
                    searchCriteriaOption[this.props.valueKey] = 'customInput';
                    options.unshift(searchCriteriaOption);
                }
            }
        }
        if (this.props.optionDisabledCallback) {
            return options.map((option) => ({
                ...option,
                disabled: this.props.optionDisabledCallback(option)
            }));
        }
        return options;
    });

    getItemDomNode(index) {
        const items = this.scrollArea.contentWrapperRef.children;
        return items[index];
    }

    handleItemHover = (e, item) => {
        const options = this.getOptions();
        if (!item.disabled) {
            this.setState({ highlightedItemIndex: options.indexOf(item) });
        }
    };

    handleKeyDown = e => {
        if (this.state.inputFocused) {
            switch (e.keyCode) {
            case keyCodes.enter:
                this.handleEnter(e);
                break;
            case keyCodes.escape:
                e.stopPropagation();
                this.toggleSelectMenu(false);
                break;
            case keyCodes.upArrow:
                this.handleArrowUp();
                break;
            case keyCodes.downArrow:
                this.handleArrowDown();
                break;
            default:
            }
        }
    };

    handleEnter = e => {
        const options = this.getOptions();
        if (this.state.isMenuShown) {
            if (this.state.highlightedItemIndex !== -1) {
                this.selectOption(options[this.state.highlightedItemIndex], e);
            } else if (options[0]) {
                this.selectOption(options[0], e);
            }
        } else {
            e.preventDefault();
            this.toggleSelectMenu(true);
        }
    };

    getNewHighlightedItemIndex = (change) => {
        const options = this.getOptions();
        let highlightedItemIndex = this.state.highlightedItemIndex;
        let tempIndex = this.state.highlightedItemIndex;
        let indexInvalid = true;
        let count = 0;
        while (indexInvalid) {
            if (change < 0) {
                tempIndex = Math.max(tempIndex + change, 0);
                indexInvalid = tempIndex !== 0;
            } else {
                tempIndex = Math.min(tempIndex + change, options.length - 1);
                indexInvalid = tempIndex !== options.length - 1;
            }
            if (!options[tempIndex].disabled) {
                highlightedItemIndex = tempIndex;
                indexInvalid = false;
            }
            count++;
            if (count > 500) {
                indexInvalid = false;
            }
        }
        return highlightedItemIndex;
    };

    handleArrowUp = () => {
        const highlightedItemIndex = this.getNewHighlightedItemIndex(-1);
        if (highlightedItemIndex === this.state.highlightedItemIndex) {
            return;
        }
        if (this.scrollArea) {
            const highlightedItem = this.getItemDomNode(highlightedItemIndex);
            if (highlightedItem) {
                const itemUpperBorderPosition = highlightedItem.offsetTop;

                if (itemUpperBorderPosition < 0) {
                    this.scrollArea.scrollYTo(this.scrollArea.state.topPosition + itemUpperBorderPosition);
                }
            }
        }

        this.toggleSelectMenu(true);
        this.setState({
            highlightedItemIndex
        });
    };

    handleArrowDown = () => {
        const highlightedItemIndex = this.getNewHighlightedItemIndex(1);
        if (highlightedItemIndex === this.state.highlightedItemIndex) {
            return;
        }
        if (this.scrollArea) {
            const highlightedItem = this.getItemDomNode(highlightedItemIndex);
            if (highlightedItem) {
                const itemRect = highlightedItem.getBoundingClientRect();
                const menuHeight = this.scrollArea.sizes.containerHeight;
                const lowerItemBorderPosition = itemRect.height + highlightedItem.offsetTop;

                if (lowerItemBorderPosition > menuHeight) {
                    const delta = lowerItemBorderPosition - menuHeight;
                    this.scrollArea.scrollYTo(this.scrollArea.state.topPosition + delta);
                }
            }
        }
        this.toggleSelectMenu(true);
        this.setState({ highlightedItemIndex });
    };

    documentOnClick = e => {
        const isKeyboardClick = e.target && typeof e.target.className === 'string'
            && e.target.className.indexOf('keyboard-button') >= 0;
        if (!isKeyboardClick && this.selectOptionsRef && !this.selectOptionsRef.contains(e.target)) {
            this.toggleSelectMenu(false);
        }
    };

    selectOption = (item, event) => {
        if (event) {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
            event.preventDefault();
        }
        if (item.disabled) {
            return;
        }

        let newValue;
        if (this.props.multiple) {
            newValue = this.props.value.slice();
            const index = newValue.findIndex(option => option[this.props.valueKey] === item[this.props.valueKey]);
            if (index > -1) {
                newValue.splice(index, 1);
                if (this.props.onItemDeselect) {
                    this.props.onItemDeselect(item);
                }
            } else {
                newValue.splice(this.props.options.indexOf(item), 0, item);
                if (this.props.onItemSelect) {
                    this.props.onItemSelect(item);
                }
            }
            this.onOuterDivFocus();
            this.setState({ searchCriteria: '' });
        } else {
            newValue = item;

            // if (this.selectInput) {
            //     this.selectInput.refs.wrappedInstance.input.blur();
            // }
            //
            // this.setState({ inputFocused: false });
            this.toggleSelectMenu(false);
            this.setState({ searchCriteria: this.props.setSearchCriteriaOnFocus ? newValue[this.props.labelKey] : '' });
        }
        if (this.props.onChange) {
            this.props.onChange(newValue);
        }
    };

    handleScrollChange = () => {
        if (this.selectRect && this.select) {
            const newSelectRect = this.select.getBoundingClientRect();
            if (this.selectRect
                && (this.selectRect.top !== newSelectRect.top || this.selectRect.left !== newSelectRect.left)) {
                this.toggleSelectMenu(false);
            }
        }
    };

    saveSelectRect = () => {
        if (this.select) {
            this.selectRect = this.select.getBoundingClientRect();
        }
    };

    toggleSelectMenu = flag => {
        if (!this.props.disabled) {
            const flagIsBool = typeof (flag) === 'boolean';
            if ((flagIsBool && flag !== this.state.isMenuShown) || !flagIsBool) {
                if (!this.state.isMenuShown) {
                    document.addEventListener('click', this.documentOnClick);
                    document.addEventListener('scrollChanged', this.handleScrollChange);
                    this.saveSelectRect();
                    if (this.select) {
                        const boundingBox = this.select.getBoundingClientRect();
                        let collapseDown = this.props.collapseDown;
                        if (typeof this.props.collapseDown !== 'boolean') {
                            collapseDown = boundingBox.top > window.innerHeight / 2;
                            if (collapseDown !== this.state.collapseDown) {
                                this.setState({ collapseDown });
                            }
                        }
                        const selectRect = this.select.getBoundingClientRect();
                        const selectWidth = selectRect.width - 2;
                        this.selectOptionsStyle = {
                            position: 'fixed',
                            zIndex: 99,
                            minWidth: selectWidth,
                            maxWidth: this.props.fixedListWidth ? selectWidth : 'calc(100% + 2px)',
                            top: collapseDown ? selectRect.top + 1 : selectRect.top + selectRect.height - 1,
                            left: selectRect.left
                        };
                    }
                } else {
                    document.removeEventListener('click', this.documentOnClick);
                    document.removeEventListener('scrollChanged', this.handleScrollChange);
                }
                if (this.state.isMenuShown) {
                    this.setState({
                        highlightedItemIndex: -1,
                        isMenuShown: false
                    });
                } else {
                    this.setState({ isMenuShown: true, highlightedItemIndex: 0 });
                }
            }
        }
    };

    clearSelection = e => {
        e.stopPropagation();
        if (this.props.onChange) {
            if (this.props.multiple) {
                this.props.onChange([]);
            } else {
                this.props.onChange(null);
            }
        }
    };

    saveRef = (c) => { this.select = c; };

    saveInputRef = (c) => { this.selectInput = c; };

    saveScrollRef = (c) => { this.scrollArea = c; };

    saveOptionsRef = (c) => { this.selectOptionsRef = c; };

    render() {
        const filteredOptions = this.getOptions();
        const collapseDown = typeof this.props.collapseDown === 'boolean'
            ? this.props.collapseDown
            : this.state.collapseDown;

        const hasValue = this.props.value
            && ((this.props.multiple && this.props.value.length > 0) || !this.props.multiple);

        const selectClass = getClassName('sten-select', this.props.className, {
            'sten-select--searchable': this.props.searchable,
            'sten-select--disabled': this.props.disabled,
            'sten-select--warning': this.props.invalid && this.props.warning,
            'sten-select--invalid': this.props.invalid && !this.props.warning,
            'sten-select--clearable': hasValue && this.props.clearable,
            'sten-select--focused': this.state.inputFocused,
            'sten-select--active': this.state.isMenuShown && filteredOptions.length > 0
        });

        const menuClass = getClassName('sten-select__menu', {
            'sten-select__menu--fixed-width': this.props.fixedListWidth,
            'sten-menu__items--collapse-down': collapseDown,
            'sten-menu__items--right-aligned': this.props.isRightAligned,
            'sten-select__menu--opened': this.state.isMenuShown && filteredOptions.length > 0
        });

        let chevronClassConfig = null;
        if (!this.props.onSearch) {
            if (this.state.isMenuShown && filteredOptions.length > 0) {
                chevronClassConfig = { 'icon-chevron-up': !collapseDown, 'icon-chevron-down': collapseDown };
            } else {
                chevronClassConfig = { 'icon-chevron-up': collapseDown, 'icon-chevron-down': !collapseDown };
            }
        }
        const sideIconClass = getClassName(
            'icon sten-select__icon',
            { 'icon-search': this.props.onSearch },
            chevronClassConfig
        );

        const inputType = this.props.searchable ? 'text' : 'button';
        return (
            <div
                ref={this.saveRef}
                className={selectClass}
                onKeyDown={this.handleKeyDown}
                onClick={this.onSelectClick}
                onBlur={this.onOuterDivBlur}
                // onFocus={this.onOuterDivFocus}
                title={this.props.disabled && this.props.disabledMessage ? this.props.disabledMessage : ''}
            >
                <span className={sideIconClass} />
                <div className="sten-select__input">
                    <Input
                        autoFocus={this.props.autoFocus}
                        autoFocusDelay={this.props.autoFocusDelay}
                        ref={this.saveInputRef}
                        disabled={this.props.disabled}
                        type={inputType}
                        onFocusChange={this.handleInputFocusChange}
                        value={this.state.searchCriteria}
                        onChange={this.onInputChange}
                        showKeyboard={this.props.showKeyboard}
                    />
                </div>
                {hasValue && this.props.clearable && (
                    <span
                        className="sten-select__icon sten-select__icon--clear icon icon-close"
                        onClick={this.clearSelection}
                    />
                )}
                {(!this.state.inputFocused || (!this.state.searchCriteria && !this.props.setSearchCriteriaOnFocus)) && (
                    <SelectValue
                        multiple={this.props.multiple}
                        placeholder={this.props.placeholder}
                        optionRenderer={this.props.optionRenderer}
                        selectedOptions={this.props.value}
                        labelKey={this.props.labelKey}
                        label={this.props.label}
                    />
                )}
                {this.state.isMenuShown && filteredOptions.length > 0 && (
                    <Portal>
                        <div style={this.selectOptionsStyle} ref={this.saveOptionsRef}>
                            <ScrollArea
                                ref={this.saveScrollRef}
                                className={menuClass}
                                onClick={this.onMenuClick}
                                stopScrollPropagation
                                useCSS3Translate={false}
                            >
                                {filteredOptions.map((option, index) => (
                                    <SelectOption
                                        searchCriteria={this.state.searchCriteria}
                                        className={this.props.optionClassName}
                                        hasValue={hasValue}
                                        multiple={this.props.multiple}
                                        selectedOptions={this.props.value}
                                        labelKey={this.props.labelKey}
                                        valueKey={this.props.valueKey}
                                        optionRenderer={this.props.optionRenderer}
                                        onClick={this.selectOption}
                                        option={option}
                                        isHighlighted={this.state.highlightedItemIndex === index}
                                        onMouseOver={this.handleItemHover}
                                        key={option[this.props.valueKey]}
                                    />
                                ))}
                            </ScrollArea>
                        </div>
                    </Portal>
                )}
            </div>
        );
    }
}

Select.propTypes = {
    autoFocus: PropTypes.bool,
    autoFocusDelay: PropTypes.number,
    chevronIconClass: PropTypes.string,
    className: PropTypes.string,
    clearable: PropTypes.bool,
    collapseDown: PropTypes.bool,
    disabled: PropTypes.bool,
    disabledMessage: PropTypes.string,
    fixedListWidth: PropTypes.bool,
    includeSearchInResults: PropTypes.bool,
    invalid: PropTypes.bool,
    isRightAligned: PropTypes.bool,
    label: PropTypes.string,
    labelKey: PropTypes.string,
    multiple: PropTypes.bool,
    name: PropTypes.string,
    onChange: PropTypes.func,
    onItemDeselect: PropTypes.func,
    onItemSelect: PropTypes.func,
    onSearch: PropTypes.func,
    optionClassName: PropTypes.string,
    optionDisabledCallback: PropTypes.func,
    optionRenderer: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.object),
    placeholder: PropTypes.string,
    searchable: PropTypes.bool,
    setSearchCriteriaOnFocus: PropTypes.bool,
    showKeyboard: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.any), PropTypes.objectOf(PropTypes.any)]),
    valueKey: PropTypes.string,
    warning: PropTypes.bool
};

Select.defaultProps = {
    autoFocus: false,
    autoFocusDelay: undefined,
    chevronIconClass: '',
    className: '',
    clearable: false,
    collapseDown: null,
    disabled: false,
    disabledMessage: '',
    fixedListWidth: false,
    includeSearchInResults: false,
    invalid: false,
    isRightAligned: false,
    label: '',
    labelKey: '',
    multiple: false,
    name: '',
    onChange: undefined,
    onItemDeselect: undefined,
    onItemSelect: undefined,
    onSearch: undefined,
    optionClassName: '',
    optionDisabledCallback: undefined,
    optionRenderer: undefined,
    options: null,
    placeholder: '',
    searchable: false,
    setSearchCriteriaOnFocus: false,
    showKeyboard: false,
    value: null,
    valueKey: '',
    warning: false
};

export default Select;
